import { format } from "date-fns";
import { TextBox } from "devextreme-react";
import {
  Column,
  DataGrid,
  Item as DataGridItem,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  Summary,
  Toolbar,
  TotalItem,
} from "devextreme-react/data-grid";
import { DateBox } from "devextreme-react/date-box";
import { SelectBox } from "devextreme-react/select-box";
import notify from "../../lib/notify";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchAwardsSummary } from "../../api/awards";
import { getProjectsAvailableForUser } from "../../api/projects";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { RoleEnum } from "../../types";
import { useScreenSize } from "../../utils/media-query";
import "./AwardsSummary.scss";

interface AwardSummaryDto {
  employeeId: number;
  employeeName: string;
  employeeSurname: string;
  projectId?: number;
  projectName?: string;
  totalAwardSum: number;
  period: string;
}

export const AwardsSummary = () => {
  const { t } = useTranslation();
  const { companyId } = useCompanyContext();
  const { user } = useAuth();

  // const companyId = user?.companyId ?? 0;
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | undefined>(
    undefined,
  );
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [data, setData] = useState<AwardSummaryDto[]>([]);
  const [totalSum, setTotalSum] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [mobileSearch, setMobileSearch] = useState("");

  const filteredData = useMemo(() => {
    const q = mobileSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter((e) =>
      [e.employeeSurname, e.employeeName, e.projectName]
        .filter(Boolean)
        .some((v) => v!.toString().toLowerCase().includes(q)),
    );
  }, [data, mobileSearch]);

  const filteredTotal = useMemo(
    () => filteredData.reduce((acc, x) => acc + (x.totalAwardSum || 0), 0),
    [filteredData],
  );

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t("awards.AwardsSummary"));
      worksheet.columns = [
        { header: t("awards.Surname"), key: "surname", width: 25 },
        { header: t("awards.Name"), key: "name", width: 25 },
        { header: t("awards.Project"), key: "project", width: 25 },
        { header: t("awards.Sum"), key: "sum", width: 18 },
      ];
      data.forEach((row) => {
        worksheet.addRow({
          surname: row.employeeSurname,
          name: row.employeeName,
          project: row.projectName,
          sum: row.totalAwardSum,
        });
      });
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `Podsumowanie_nagrod_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
    } catch (err) {
      notify(`${t("Error:")} ${err || t("Unknown error")}`, "error");
    }
  };

  const formatMoney = (v: number) =>
    new Intl.NumberFormat("pl-PL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v || 0);

  const loadProjects = useCallback(async () => {
    try {
      // if (!companyId || !user?.employeeId) {
      //   return;
      // }

      const res = await getProjectsAvailableForUser(
        !user?.roles?.includes(RoleEnum.AdministratorGlowny) || companyId === 0
          ? undefined
          : companyId,
      );

      const list = res.map((p: any) => ({
        id: p.id,
        name: p.acronym ?? p.name ?? "",
      }));

      const withAll = [{ id: 0, name: t("awards.Allprojects") }, ...list];
      setProjects(withAll);
      setSelectedProject(0);
    } catch {
      notify(t("common.errorOccurredWhileDataFetching"), "error", 3000);
    }
  }, [companyId, t, user?.roles]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const monthStr = format(selectedMonth, "yyyy-MM");
      const projectIdParam =
        selectedProject !== 0 ? selectedProject : undefined;

      const res = await fetchAwardsSummary(companyId, projectIdParam, monthStr);

      if (res.isOk) {
        setData(res.data.data);
        setTotalSum(res.data.totalSum);
      } else {
        notify(res.error, "error", 4000);
      }
    } catch {
      notify(t("common.errorOccurredWhileDataFetching"), "error", 4000);
    } finally {
      setLoading(false);
    }
  }, [companyId, selectedProject, selectedMonth, t]);

  useEffect(() => {
    if (companyId > 0 && user?.employeeId) {
      loadProjects();
    }
  }, [companyId, user?.employeeId, loadProjects]);

  useEffect(() => {
    if (selectedProject !== undefined) {
      loadSummary();
    }
  }, [selectedProject, selectedMonth, loadSummary]);

  if (isMobile) {
    return (
      <div className="p-4 bg-gray-50 dark:bg-[#121212] min-h-screen">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 mb-3">
          {t("awards.AwardsSummary")}
        </h2>
        <div className="sticky top-0 z-10 flex flex-col gap-2 py-2 bg-gray-50 dark:bg-[#121212]">
          <SelectBox
            dataSource={projects}
            value={selectedProject}
            valueExpr="id"
            displayExpr="name"
            labelMode="static"
            label={t("awards.Project")}
            onValueChange={(val) => {
              if (val === null || val === undefined) {
                setSelectedProject(0);
              } else {
                const parsed = typeof val === "number" ? val : Number(val);
                setSelectedProject(Number.isNaN(parsed) ? 0 : parsed);
              }
            }}
            searchEnabled
            width="100%"
          />
          <DateBox
            type="date"
            labelMode="static"
            label={t("awards.Month")}
            value={selectedMonth}
            displayFormat="MMMM yyyy"
            onValueChanged={(e) => setSelectedMonth(e.value)}
            pickerType="calendar"
            width="100%"
          />
          <TextBox
            value={mobileSearch}
            onValueChanged={(e) => setMobileSearch(e.value || "")}
            placeholder={t("searchPlaceholder")}
            showClearButton
            width="100%"
            mode="search"
          />
          <div className="flex items-center justify-between rounded-xl px-4 py-3 bg-emerald-600 text-white shadow">
            <span className="text-sm uppercase tracking-wide opacity-90">
              {t("awards.Summary")}
            </span>
            <span className="text-lg font-bold tabular-nums">
              {formatMoney(filteredTotal)} zł
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-2 pb-24">
          {filteredData.length === 0 ? (
            <div
              className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
              role="alert"
            >
              {t("noSubordinateEmployees")}
            </div>
          ) : (
            filteredData
              .slice()
              .sort((a, b) => (b.totalAwardSum || 0) - (a.totalAwardSum || 0))
              .map((row, idx) => (
                <div
                  key={`${row.employeeId}-${row.projectId ?? "none"}-${idx}`}
                  className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#1e1e1e] shadow-sm overflow-hidden"
                >
                  <div className="flex items-start justify-between gap-3 px-4 pt-3 pb-3">
                    <div className="min-w-0">
                      <div className="text-base font-bold break-words">
                        {`${row.employeeSurname || ""} ${
                          row.employeeName || ""
                        }`.trim()}
                      </div>
                      {row.projectName && (
                        <div className="text-xs opacity-70 mt-0.5 break-words">
                          {row.projectName}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[11px] uppercase opacity-70">
                        {t("awards.Sum")}
                      </div>
                      <div className="text-lg font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
                        {formatMoney(row.totalAwardSum)} zł
                      </div>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
        <LoadPanel enabled={loading} showIndicator shading />
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">
            {t("awards.AwardsSummary")}
          </h2>
        </div>

        {/* FILTRY */}
        <div className="flex flex-wrap gap-6 mb-6">
          <div className="flex flex-col">
            <SelectBox
              dataSource={projects}
              value={selectedProject}
              valueExpr="id"
              displayExpr="name"
              labelMode="static"
              label={t("awards.Project")}
              onValueChange={(val) => {
                if (val === null || val === undefined) {
                  setSelectedProject(0);
                } else {
                  const parsed = typeof val === "number" ? val : Number(val);
                  setSelectedProject(Number.isNaN(parsed) ? 0 : parsed);
                }
              }}
              searchEnabled
              width={250}
            />
          </div>

          <div className="flex flex-col">
            <DateBox
              type="date"
              labelMode="static"
              label={t("awards.Month")}
              value={selectedMonth}
              displayFormat="MMMM yyyy"
              onValueChanged={(e) => setSelectedMonth(e.value)}
              pickerType="calendar"
              width={200}
            />
          </div>
        </div>

        {/* TABELA */}
        <div className="rounded-lg overflow-hidden border border-gray-200">
          <DataGrid
            dataSource={data}
            showBorders={false}
            rowAlternationEnabled
            wordWrapEnabled
            height="65vh"
            className="text-gray-800"
          >
            <Toolbar>
              <DataGridItem
                location="after"
                locateInMenu="auto"
                widget="dxButton"
                options={{
                  icon: "exportxlsx",
                  hint: t("Export all"),
                  stylingMode: "contained",
                  onClick: exportToExcel,
                  elementAttr: {
                    class:
                      "bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-medium px-4 py-2 rounded-lg shadow-md transition duration-150 ease-in-out mr-2",
                  },
                }}
              />
            </Toolbar>
            <FilterRow visible />
            <HeaderFilter visible />

            <Column
              dataField="employeeSurname"
              caption={t("awards.Surname")}
              width={180}
              cssClass="font-medium"
            />
            <Column
              dataField="employeeName"
              caption={t("awards.Name")}
              width={160}
            />
            <Column
              dataField="projectName"
              caption={t("awards.Project")}
              width={160}
            />
            <Column
              dataField="totalAwardSum"
              caption={t("awards.Sum")}
              dataType="number"
              format="#,##0.00"
              alignment="right"
              cssClass="font-semibold text-right"
            />

            <Summary>
              <TotalItem
                column="totalAwardSum"
                summaryType="sum"
                displayFormat={t("{0}") ?? "{0}"}
                valueFormat="#,##0.00"
                cssClass="custom-total"
                alignment="right"
                customizeText={(e) =>
                  `${t("awards.Summary")}: ${e.valueText} zł`
                }
              />
            </Summary>
          </DataGrid>
        </div>
      </div>

      <LoadPanel enabled={loading} showIndicator shading />
    </div>
  );
};
